import { useState } from "react";
import type { Dispatch, SetStateAction } from 'react';

import {
    Box, Collapse,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    TextField,
    Grid
} from "@mui/material";
import Select from "@mui/material/Select";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import MeasurementList from "./MeasurementList";
import {useTranslation} from "react-i18next";
import {MeasurementPoints, TemplateData} from "../../pages/app/types.ts";

interface MeasurementPointStepProps {
    mode: string;
    template: TemplateData;
    setTemplate: Dispatch<SetStateAction<TemplateData>>;
}

const MeasurementPointStep = ({ mode, template, setTemplate }: MeasurementPointStepProps) => {
    const {t} = useTranslation();
    const [pointInputValue, setPointInputValue] = useState('');
    const [expand, setExpand] = useState<{ [key: number]: boolean }>({});
    const [norm, setNorm] = useState('');
    const [maxN, setMaxN] = useState('');

    const openPoint = (id: number) => {
        setExpand({ ...expand, [id]: !expand[id] });
    }

    const addPoint = () => {
        if (pointInputValue !== '' && norm !== '' && maxN !== '') {
            const nextId = template.measurementPoints.length + 1;
            const newPoint = {
                id: nextId,
                label: pointInputValue,
                norm,
                maxN,
                measurements: []
            };

            const newPoints = [...template.measurementPoints, newPoint];
            setTemplate((prev: TemplateData) => ({ ...prev, measurementPoints: newPoints }));

            setExpand({ ...expand, [nextId]: false });

            setPointInputValue('');
            setNorm('');
            setMaxN('');
        } else {
            alert(t('stepper.step.measurementPoint.addError'));
        }
    };


    const deletePoint = (idToDelete: number) => {
        const filteredPoints = template.measurementPoints.filter(point => point.id !== idToDelete);

        const newExpand: { [key: number]: boolean } = {};

        const reindexedPoints = filteredPoints.map((point, index) => {
            const newId = index + 1;

            newExpand[newId] = expand[point.id] ?? false;

            const reindexedMeasurements = point.measurements.map((m, mIndex) => ({
                ...m,
                id: `${newId}-${mIndex + 1}`
            }));

            return {
                ...point,
                id: newId,
                measurements: reindexedMeasurements
            };
        });

        setExpand(newExpand);
        setTemplate((prev: TemplateData) => ({ ...prev, measurementPoints: reindexedPoints }));
    };


    const renderMeasurementPoint = (point: MeasurementPoints) => (
        <>
            <ListItem
                sx={{ backgroundColor: 'white' }}
                component="div"
                onClick={() => openPoint(point.id)}
                key={point.id}
            >
                <ListItemText primary={point.label} />
                {expand[point.id] ? <ExpandLess /> : <ExpandMore />}
                {mode !== "overview" && (
                    <IconButton edge="end" aria-label="delete" onClick={() => deletePoint(point.id)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </ListItem>
            <Collapse in={expand[point.id]} timeout="auto" unmountOnExit>
                <MeasurementList
                    template={template}
                    setTemplate={setTemplate}
                    pointId={point.id}
                    mode={mode}
                />
            </Collapse>
        </>
    );


    return (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {mode != "overview" && (
                <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid size={{xs: 12, sm: 12}}>
                        <TextField
                            value={pointInputValue}
                            onChange={(e) => setPointInputValue(e.target.value)}
                            placeholder={t('stepper.step.measurementPoint.placeholder')}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <FormControl fullWidth sx={{ minWidth: 150}}>
                            <InputLabel>Norm</InputLabel>
                            <Select
                                value={norm}
                                label="Norm"
                                onChange={(event) => setNorm(event.target.value as string)}
                            >
                                <MenuItem value={'EN 12453:2017'}>EN 12453:2017</MenuItem>
                                <MenuItem value={'EN 16005:2012'}>EN 16005:2012</MenuItem>
                                <MenuItem value={'EN 14120:2015'}>EN 14120:2015</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <FormControl fullWidth sx={{ minWidth: 150 }}>
                            <InputLabel>Max N</InputLabel>
                            <Select
                                value={maxN}
                                label="Max N"
                                onChange={(event) => setMaxN(event.target.value as string)}
                            >
                                <MenuItem value={'400N'}>400N</MenuItem>
                                <MenuItem value={'1400N'}>1400N</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12, sm: 12}} display="flex" justifyContent="center" alignItems="center">
                        <IconButton onClick={addPoint} style={{color: '#53b2ef', backgroundColor: 'white'}} sx={{ height: '100%' }}>
                            <AddCircleIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            )}
            <List component="div" disablePadding>
                {template.measurementPoints.map((point) => renderMeasurementPoint(point))}
            </List>
        </Box>
    );
}

export default MeasurementPointStep;
